import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Loader2, Save, User, Building2, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

interface Props {
  open: boolean;
  type: string;
  onClose: () => void;
  onSave: (data: {
    title: string;
    type: string;
    category: "personal" | "clinic";
    doctor_id: string;
    clinic_id: string;
  }) => Promise<void>;
  suggestedTitle?: string;
}

const TYPE_LABELS: Record<string, string> = {
  complaint: "Complaints",
  medicine: "Medicines",
  investigation: "Investigations",
  advice: "Advice",
  diagnosis: "Diagnosis",
  quick_notes: "Quick Notes",
};

const categoryOptions = [
  { value: "personal" as const, label: "Personal", icon: User,     desc: "Only visible to you" },
  { value: "clinic"   as const, label: "Clinic",   icon: Building2, desc: "Shared with clinic" },
];

export default function SaveTemplateModal({ open, type, onClose, onSave, suggestedTitle }: Props) {
  const [title, setTitle]       = useState("");
  const [category, setCategory] = useState<"personal" | "clinic">("personal");
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState("");
  const { currentUser }         = useAuth();

  useEffect(() => {
    if (open) {
      setTitle(suggestedTitle || "");
      setCategory("personal");
      setSaving(false);
      setSaved(false);
      setError("");
    }
  }, [open, suggestedTitle]);

  const handleSave = async () => {
    if (!title.trim()) { setError("Template name is required"); return; }
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) {
      setError("User session missing. Please log in again.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await onSave({
        title: title.trim(),
        type,
        category,
        doctor_id: currentUser.doctor_id,
        clinic_id: currentUser.clinic_id,
      });
      setSaved(true);
      setTimeout(() => onClose(), 1200);
    } catch (err: any) {
      setError(err?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  return (
      <Dialog open={open} onOpenChange={() => !saving && onClose()}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden gap-0">

          {/* Header */}
          <DialogHeader className="px-5 py-4 border-b border-border">
            <DialogTitle className="text-sm font-semibold">
              Save {TYPE_LABELS[type] || "Template"}
            </DialogTitle>
          </DialogHeader>

          {/* Body */}
          <div className="px-5 py-4 space-y-4">

            {/* Error */}
            {error && (
                <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
                  <X className="w-3.5 h-3.5 flex-shrink-0" />
                  {error}
                </div>
            )}

            {/* Name */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Template Name <span className="text-destructive">*</span>
              </Label>
              <Input
                  autoFocus
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setError(""); }}
                  placeholder={`e.g. ${TYPE_LABELS[type] || "Template"} for Fever`}
                  disabled={saving || saved}
                  className={cn("h-9 text-sm", error && !title.trim() && "border-destructive")}
                  onKeyDown={(e) => e.key === "Enter" && !saving && !saved && handleSave()}
              />
            </div>

            {/* Visibility */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Visibility
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {categoryOptions.map(({ value, label, icon: Icon, desc }) => (
                    <button
                        key={value}
                        type="button"
                        disabled={saving || saved}
                        onClick={() => setCategory(value)}
                        className={cn(
                            "flex flex-col items-center gap-1 p-3 rounded-lg border text-center transition-all",
                            category === value
                                ? "border-primary bg-primary/5 text-primary"
                                : "border-border text-muted-foreground hover:border-ring/60 hover:text-foreground"
                        )}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-xs font-medium">{label}</span>
                      <span className="text-[10px] leading-tight opacity-70">{desc}</span>
                    </button>
                ))}
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={saving}
            >
              Cancel
            </Button>
            <Button
                type="button"
                size="sm"
                disabled={saving || saved || !title.trim()}
                onClick={handleSave}
                className="min-w-[110px]"
            >
              {saved ? (
                  <><CheckCircle className="w-3.5 h-3.5 mr-1.5" />Saved!</>
              ) : saving ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</>
              ) : (
                  <><Save className="w-3.5 h-3.5 mr-1.5" />Save Template</>
              )}
            </Button>
          </div>

        </DialogContent>
      </Dialog>
  );
}